/**
 * ConfigLoggingPage
 *
 * Admin page to configure the logging adapter and log level for this org.
 * Reads/writes organisation/{org_id}/config/logging in Firestore via
 * the /admin/config/logging endpoints.
 *
 * Note: adapter_type changes require a service restart to take effect.
 *
 * Route: /settings/config/logging  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton } from "../../components/Button/Buttons";
import Select from "../../components/Inputs/Select";
import Loader from "../../components/Loader/Loader";
import { getServiceConfig, updateServiceConfig } from "../../api/adminConfig";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import { isDirty } from "../../utils/isDirty";

const ADAPTER_OPTIONS = [
  { value: "json",     label: "JSON (structured — recommended for cloud)" },
  { value: "standard", label: "Standard (human-readable — for local dev)" },
];

const LOG_LEVEL_OPTIONS = [
  { value: "debug",   label: "DEBUG"   },
  { value: "info",    label: "INFO (default)" },
  { value: "warning", label: "WARNING" },
  { value: "error",   label: "ERROR"   },
];

const EMPTY_FORM = {
  adapter_type: "json",
  log_level:    "info",
};

const ConfigLoggingPage = () => {
  const { showFeedback } = useFeedback();
  const orgAvailable = UIStore((s) => s.orgAvailable);

  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(EMPTY_FORM);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServiceConfig("logging");
      if (data.configured) {
        const loaded = {
          adapter_type: data.adapter_type ?? "json",
          log_level:    data.log_level    ?? "info",
        };
        setForm(loaded);
        setOriginalForm(loaded);
      } else {
        setForm(EMPTY_FORM);
        setOriginalForm(EMPTY_FORM);
      }
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load logging config.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    if (orgAvailable) fetchConfig();
  }, [orgAvailable, fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateServiceConfig("logging", form);
      setOriginalForm(form);
      showFeedback("Logging config saved. Adapter type changes require a service restart.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save logging config.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsPageShell
      title="Logging Configuration"
      description="Configure the log format and verbosity. Adapter type changes take effect on next service restart."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6 max-w-lg">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Log Format <span className="font-normal text-ink-gray-400">(requires restart)</span>
            </label>
            <Select
              value={form.adapter_type}
              onChange={(v) => setForm((prev) => ({ ...prev, adapter_type: v }))}
              options={ADAPTER_OPTIONS}
            />
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
              Use JSON in production for structured log aggregation (Cloud Logging, Datadog, etc.). Standard is human-readable but harder to parse in log aggregators.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Log Level
            </label>
            <Select
              value={form.log_level}
              onChange={(v) => setForm((prev) => ({ ...prev, log_level: v }))}
              options={LOG_LEVEL_OPTIONS}
            />
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
              Log level changes take effect immediately across running API instances.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <PrimaryButton onClick={handleSave} disabled={saving || !isDirty(form, originalForm)}>
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </SettingsPageShell>
  );
};

export default ConfigLoggingPage;
